// Rullskidspel: roller-ski through Stockholm in three lanes against oncoming
// traffic, for as long as you can. Isometric three.js scene rendered at low
// resolution and scaled up with nearest-neighbour filtering, so everything
// comes out as chunky pixels.
//
// The player stays at z = 0 and the world moves past (+z is behind, -z ahead).
// Track position `s` (metres from the start) maps to z = -(s - dist).
import * as THREE from "three";
import { sfx, SfxName } from "./sound";

export type VehicleKind = "car" | "taxi" | "van" | "bus" | "bike" | "scooter" | "sport" | "tram";
export type PowerKind = "bun" | "soup" | "shield" | "slow";
export type Phase = "ready" | "count" | "play" | "over";

export interface HudState {
  score: number;
  dist: number;    // metres
  buns: number;
  strikes: number; // cyclist/scooter hits since the run began
  shield: number;  // seconds left
  slow: number;    // seconds left
  stage: string;
  count: number;   // countdown seconds left (0 once running)
  cross: boolean;  // people on (or about to step onto) the crossing ahead
}
export interface GameOver { score: number; dist: number; buns: number }
export interface GameCallbacks {
  onHud: (s: HudState) => void;
  onOver: (r: GameOver) => void;
}

export const SOFT_STRIKES = 2;  // cyclist/scooter hits that end the run
export const POWER_SECS = 5;
const TOP_SPEED = 48; // m/s

type Side = "city" | "park" | "quay" | "water";
interface Stage { from: number; name: string; left: Side; right: Side }
// One lap of scenery; it repeats for as long as the run lasts
const LAP = 2000;
const STAGES: Stage[] = [
  { from: 0, name: "Slussen", left: "city", right: "quay" },
  { from: 420, name: "Skeppsbron", left: "city", right: "quay" },
  { from: 780, name: "Kungsträdgården", left: "city", right: "park" },
  { from: 1040, name: "Strandvägen", left: "city", right: "quay" },
  { from: 1760, name: "Djurgårdsbron", left: "water", right: "water" },
  { from: 1900, name: "Djurgården", left: "park", right: "park" },
];
const stageAt = (s: number) => {
  const p = s < 0 ? 0 : s % LAP;
  let st = STAGES[0];
  for (const x of STAGES) if (p >= x.from) st = x;
  return st;
};

const LANES = [-2.2, 0, 2.2];
const ROAD_HALF = 3.6;
const WALK = 2.2;                   // sidewalk width
const EDGE = ROAD_HALF + WALK;      // where the street ends on each side
const CHUNK = 16;
const NCH = 12;
const BACK = 45;                    // recycle things this far behind the player
const AHEAD = 130;                  // spawn traffic this far ahead

const SIZE: Record<VehicleKind, { len: number; w: number }> = {
  car: { len: 3.8, w: 1.7 },
  taxi: { len: 3.8, w: 1.7 },
  van: { len: 4.8, w: 1.9 },
  bus: { len: 10.5, w: 2.3 },
  bike: { len: 1.8, w: 0.6 },
  scooter: { len: 1.1, w: 0.5 },
  sport: { len: 4.2, w: 1.8 },
  tram: { len: 22, w: 2.4 },
};
// Speeding cars ("smitare") race through the gaps between rows
const SPORT_BOOST = 1.6;
// Djurgårdslinjen: trams in the middle lane along these stretches
const TRAM_STAGES = ["Strandvägen", "Djurgårdsbron", "Djurgården"];

const FACADES = ["#d9a44c", "#c8693f", "#e8c66c", "#ead9bd", "#b5503d", "#a7bda4", "#dc9670", "#e3b98f"];
const ROOFS = ["#5d8f7c", "#3b3d42", "#6a4a3a", "#5d8f7c"];
const AWNINGS = ["#2e6b4f", "#b83a30", "#24476b", "#e2b31a"];
const LEAVES = ["#d8772c", "#e6a532", "#c95a26", "#7a9a3a", "#e08a2e"];
const CARS = ["#e9e6df", "#c23b30", "#2f5d9e", "#1f2328", "#a9adb3", "#3d7a55"];
const JERSEYS = ["#2f5d9e", "#c23b30", "#3d7a55", "#e9e6df", "#7a4fa0"];
const GRASS = "#4f7a35";
const WATER = "#3f7ea8";

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T>(xs: T[]): T => xs[Math.floor(Math.random() * xs.length)];

// A vehicle may change lane once: it signals for SIGNAL_SECS (blinkers, or a
// lean for bikes), then slides across at SWERVE_SPEED
interface Traffic {
  g: THREE.Group; len: number; w: number; soft: boolean; hit: boolean;
  v: number;              // own speed over the ground (drops to 0 at a crossing)
  lane: number;
  swerveZ: number | null; // starts signalling once it gets this close
  signal: number;         // seconds left of signalling
  dir: number;
  blink: Record<number, THREE.Mesh[]>; // blinkers per world side (-1 left, 1 right)
  boost: number;          // speed relative to the rest of the traffic
  cue: SfxName | null;    // sound to play when it comes on screen
  rider: THREE.Object3D | null; // cyclist/scooter rider, who can be knocked off
}
// A knocked-off rider tumbling through the air
interface Flyer { g: THREE.Object3D; vx: number; vy: number; vz: number; spin: THREE.Vector3; age: number }
const GRAV = 22;

// Roadworks: one lane or two are dug up, one has a ramp up and over a trench.
// Traffic is kept out of the stretch while the player passes.
const SITE_LEN = 12;
const RAMP_LEN = 6;
const RAMP_H = 1.5;
interface Site { g: THREE.Group; ramp: number; blocked: number[] }
// Drivers signal for less time the longer the run goes on
const SIGNAL_SECS = 1.1;
const SIGNAL_MIN = 0.75;
const SWERVE_SPEED = 3.6;

// Zebra crossing: traffic leaves a clear stretch around it, and as it comes
// on screen a line of people walks across. They are spaced so they block at
// most two lanes at a time, so there is always a gap, but it keeps moving.
interface Ped { g: THREE.Group; dir: number; wait: number }
interface Crossing { g: THREE.Group; peds: Ped[]; state: "wait" | "stop" | "walk" | "done" }
const CROSS_D = 3;        // depth of the stripes
const PED_SPEED = 1.6;
const CROSS_SECS = 2.4;   // people start walking about this long before you get there
interface Power { g: THREE.Group; kind: PowerKind }

// Shared geometry/materials so the many small boxes stay cheap
class Kit {
  box = new THREE.BoxGeometry(1, 1, 1);
  private mats = new Map<string, THREE.Material>();
  private texs: THREE.Texture[] = [];

  mat(c: string): THREE.Material {
    let m = this.mats.get(c);
    if (!m) {
      m = new THREE.MeshLambertMaterial({ color: c });
      this.mats.set(c, m);
    }
    return m;
  }

  // Unlit material, so blinkers stay bright in the shade
  glow(c: string, opacity = 1): THREE.Material {
    const key = "glow" + c + opacity;
    let m = this.mats.get(key);
    if (!m) {
      m = new THREE.MeshBasicMaterial({ color: c, transparent: opacity < 1, opacity, depthWrite: opacity >= 1 });
      this.mats.set(key, m);
    }
    return m;
  }

  // Box with its bottom at y
  blk(p: THREE.Object3D, c: string, x: number, y: number, z: number, w: number, h: number, d: number, shadow = true) {
    const m = new THREE.Mesh(this.box, this.mat(c));
    m.scale.set(w, h, d);
    m.position.set(x, y + h / 2, z);
    m.castShadow = shadow;
    m.receiveShadow = true;
    p.add(m);
    return m;
  }

  // One window per 8×8 texel tile, repeated over a facade
  facade(c: string): THREE.Material {
    const key = "facade" + c;
    let m = this.mats.get(key);
    if (!m) {
      const cv = document.createElement("canvas");
      cv.width = cv.height = 8;
      const g = cv.getContext("2d")!;
      g.fillStyle = c;
      g.fillRect(0, 0, 8, 8);
      g.fillStyle = "rgba(0,0,0,0.12)";
      g.fillRect(0, 7, 8, 1);
      g.fillStyle = "#f4efe4";
      g.fillRect(2, 1, 4, 5);
      g.fillStyle = "#2c3b4c";
      g.fillRect(3, 2, 2, 4);
      const t = new THREE.CanvasTexture(cv);
      t.magFilter = THREE.NearestFilter;
      t.minFilter = THREE.NearestFilter;
      t.generateMipmaps = false;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      this.texs.push(t);
      m = new THREE.MeshLambertMaterial({ map: t });
      this.mats.set(key, m);
    }
    return m;
  }

  dispose() {
    this.box.dispose();
    this.mats.forEach((m) => m.dispose());
    this.texs.forEach((t) => t.dispose());
  }
}

// How the player's skier looks; picked on the start screen
export type Headwear = "helmet" | "beanie" | "cap" | "band";
export type Glasses = "none" | "black" | "mirror";
export type Pack = "none" | "belt" | "vest";
export interface Look {
  jersey: string; pants: string; skin: string;
  head: Headwear; headColor: string;
  glasses: Glasses; skis: string; poles: string; pack: Pack;
}
export const DEFAULT_LOOK: Look = {
  jersey: "#e2b31a", pants: "#23324f", skin: "#f1c9a5",
  head: "helmet", headColor: "#f4f1ea",
  glasses: "black", skis: "#1f2a44", poles: "#c9ced6", pack: "none",
};

interface Fan { g: THREE.Group; arms: THREE.Object3D[]; ph: number; z: number }
interface Chunk { g: THREE.Group; n: number; geos: THREE.BufferGeometry[]; fans: Fan[] }

export class SkiGame {
  private kit = new Kit();
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private cam = new THREE.OrthographicCamera(-10, 10, 10, -10, -100, 200);
  private sun = new THREE.DirectionalLight("#fff2dc", 0.8);
  private chunks: Chunk[] = [];
  private traffic: Traffic[] = [];
  private powers: Power[] = [];
  private crossing: Crossing | null = null;
  private skier = new THREE.Group();
  private body = new THREE.Group();
  private arms: THREE.Group[] = [];
  private bubble: THREE.Mesh;
  private look: Look = DEFAULT_LOOK;
  private closeUp = false; // camera zoomed in on the skier while picking gear
  private spin = 0;         // turntable angle in the close-up
  private bubbleMat = new THREE.MeshBasicMaterial({ color: "#7fd3ff", transparent: true, opacity: 0.35, depthWrite: false });
  private raf = 0;
  private last = 0;
  private ro: ResizeObserver;

  private phase: Phase = "ready";
  private time = 0;
  private dist = 0;
  private speed = 0;
  private frontZ = 0;   // far edge of the furthest traffic row
  private nextPower = 0; // distance at which the next power-up appears
  private nextCross = 0; // distance at which the next crossing appears
  controls: "swipe" | "tap" = "swipe";
  private swipe: { id: number; x: number; y: number } | null = null;
  private countT = 0;
  private lane = 1;
  private x = 0;
  private buns = 0;
  private flyers: Flyer[] = [];
  private site: Site | null = null;
  private nextSite = 0;
  private jumpY = 0;
  private jumpV = 0;
  private airborne = false;
  private onRamp = false;
  private ambT = 0;
  private lastDt = 1 / 60;
  private anim = 0;
  private strikes = 0;
  private shield = 0;
  private slow = 0;
  private inv = 0;      // short grace period after a hit
  private stumble = 0;
  private fall = 0;     // game-over tumble
  private pole = 0;     // poling animation phase
  private hudT = 0;
  private lastCount = 0; // countdown second last beeped
  private tickT = 0;     // crossing signal ticker
  private hornT = 0;     // keeps horns from piling up

  constructor(private host: HTMLElement, private cb: GameCallbacks) {
    this.renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(1);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.BasicShadowMap;
    const cv = this.renderer.domElement;
    cv.className = "skigame__canvas";
    host.appendChild(cv);

    this.scene.background = new THREE.Color(GRASS);
    this.scene.add(new THREE.HemisphereLight("#e4edf5", "#6b5a45", 0.75));
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(1024, 1024);
    const sc = this.sun.shadow.camera;
    sc.left = sc.bottom = -45;
    sc.right = sc.top = 45;
    sc.near = 1;
    sc.far = 90;
    this.sun.position.set(-14, 28, -24);
    this.sun.target.position.set(0, 0, -16);
    this.scene.add(this.sun, this.sun.target);

    this.buildSkier();
    this.bubble = new THREE.Mesh(this.kit.box, this.bubbleMat);
    this.bubble.scale.set(1.5, 2.4, 2.4);
    this.bubble.position.y = 1.15;
    this.skier.add(this.bubble);
    this.scene.add(this.skier);
    for (let i = 0; i < NCH; i++) {
      const c: Chunk = { g: new THREE.Group(), n: 0, geos: [], fans: [] };
      this.scene.add(c.g);
      this.chunks.push(c);
    }
    this.reset();

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.resize();
    window.addEventListener("keydown", this.onKey);
    cv.addEventListener("pointerdown", this.onDown);
    cv.addEventListener("pointermove", this.onMove);
    cv.addEventListener("pointerup", this.onUp);
    cv.addEventListener("pointercancel", this.onUp);
    this.raf = requestAnimationFrame(this.loop);
  }

  // ── public API ──
  start() {
    this.reset();
    this.phase = "count";
    this.countT = 3;
    this.lastCount = 0;
    this.tickT = 0;
    this.hornT = 0;
  }

  setLook(look: Look) {
    this.look = look;
    this.buildSkier();
  }

  // Zooms in on the skier, low on the screen, so gear can be seen while picking
  setCloseUp(on: boolean) {
    this.closeUp = on;
    this.resize();
  }

  steer(dir: -1 | 1) {
    if (this.phase !== "play" && this.phase !== "count") return;
    const lane = Math.max(0, Math.min(2, this.lane + dir));
    if (lane !== this.lane && this.phase === "play") sfx.play("swoosh");
    this.lane = lane;
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
    window.removeEventListener("keydown", this.onKey);
    const cv = this.renderer.domElement;
    cv.removeEventListener("pointerdown", this.onDown);
    cv.removeEventListener("pointermove", this.onMove);
    cv.removeEventListener("pointerup", this.onUp);
    cv.removeEventListener("pointercancel", this.onUp);
    this.chunks.forEach((c) => c.geos.forEach((g) => g.dispose()));
    this.bubbleMat.dispose();
    this.kit.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  // ── setup ──
  private reset() {
    this.time = 0;
    this.dist = 0;
    this.speed = 0;
    this.frontZ = -45; // a clear stretch before the first car
    this.nextPower = 40;
    this.lane = 1;
    this.x = 0;
    this.buns = 0;
    this.strikes = 0;
    this.shield = 0;
    this.slow = 0;
    this.inv = 0;
    this.stumble = 0;
    this.fall = 0;
    this.traffic.forEach((t) => this.scene.remove(t.g));
    this.powers.forEach((p) => this.scene.remove(p.g));
    this.traffic = [];
    this.powers = [];
    if (this.crossing) this.scene.remove(this.crossing.g);
    this.crossing = null;
    this.nextCross = 220;
    this.flyers.forEach((f) => this.scene.remove(f.g));
    this.flyers = [];
    if (this.site) this.scene.remove(this.site.g);
    this.site = null;
    this.nextSite = 330;
    this.jumpY = this.jumpV = 0;
    this.airborne = this.onRamp = false;
    this.ambT = 2;
    this.skier.position.set(0, 0, 0);
    this.skier.rotation.set(0, 0, 0);
    this.skier.visible = true;
    const n0 = Math.floor(-BACK / CHUNK);
    this.chunks.forEach((c, i) => {
      c.n = n0 + i;
      this.buildChunk(c);
    });
    this.phase = "ready";
    this.emitHud();
  }

  private resize() {
    const w = this.host.clientWidth || 1;
    const h = this.host.clientHeight || 1;
    // Chunky pixels, but finer on big screens: phones stay at 2×, a desktop
    // window lands on 3× instead of 5×
    const px = Math.min(4, Math.max(2, Math.round(Math.sqrt(w * h) / 450)));
    // Each game pixel must cover a whole number of *device* pixels. Otherwise
    // (125 %/150 % display scaling, or a size that doesn't divide evenly) the
    // pixels come out 2 and 3 wide by turns and the scene shimmers when moving.
    const dpr = window.devicePixelRatio || 1;
    const dev = Math.max(1, Math.round(px * dpr));
    const rw = Math.ceil((w * dpr) / dev);
    const rh = Math.ceil((h * dpr) / dev);
    this.renderer.setSize(rw, rh, false);
    // Slightly larger than the host when it doesn't divide evenly; the overflow is clipped
    Object.assign(this.renderer.domElement.style, { width: `${(rw * dev) / dpr}px`, height: `${(rh * dev) / dpr}px` });
    const aspect = rw / rh;
    const halfW = aspect >= 1 ? 12.5 * aspect : 9.5;
    const halfH = halfW / aspect;
    Object.assign(this.cam, { left: -halfW, right: halfW, top: halfH, bottom: -halfH });
    if (this.closeUp) {
      // Skier beside the gear panel on wide screens, above it on tall ones
      const z = aspect >= 1 ? 4 : 5;
      const hw = halfW / z;
      const hh = halfH / z;
      if (aspect >= 1) Object.assign(this.cam, { left: -hw * 1.35, right: hw * 0.65, top: hh, bottom: -hh });
      else Object.assign(this.cam, { left: -hw, right: hw, top: hh * 0.55, bottom: -hh * 1.45 });
    }
    this.cam.updateProjectionMatrix();
    // Classic 45° isometric on wide screens; on tall phones the camera swings
    // round behind the skier so the road runs up the screen instead of across it
    const yaw = THREE.MathUtils.degToRad(THREE.MathUtils.clamp(18 + (aspect - 0.5) * 54, 18, 45));
    const pitch = Math.atan(34 / 42.4);
    const back = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));  // towards the camera
    const right = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw)); // screen right
    // Put the skier low and to the left so most of the screen shows what's ahead
    const look = this.closeUp
      ? new THREE.Vector3(0, 1.2, 0)
      : new THREE.Vector3()
          .addScaledVector(back, (-0.45 * halfH) / Math.sin(pitch))
          .addScaledVector(right, 0.4 * halfW);
    this.cam.position.copy(look).addScaledVector(back, 42.4).setY(34);
    this.cam.lookAt(look);
  }

  private buildSkier() {
    const k = this.kit;
    const g = this.skier;
    const L = this.look;
    g.clear();
    this.body.clear();
    this.arms = [];
    if (this.bubble) g.add(this.bubble);
    for (const x of [-0.22, 0.22]) {
      k.blk(g, L.skis, x, 0.12, 0, 0.12, 0.06, 1.3);  // ski
      for (const z of [-0.55, 0.55]) k.blk(g, "#16181c", x, 0, z, 0.14, 0.16, 0.16); // wheels
      k.blk(g, L.pants, x, 0.18, 0.05, 0.2, 0.75, 0.22); // leg
    }
    this.body.position.set(0, 0.9, 0.05);
    g.add(this.body);
    k.blk(this.body, L.jersey, 0, 0, 0, 0.62, 0.66, 0.34);  // jersey
    k.blk(this.body, "#1a1a1a", 0, 0.3, 0.001, 0.64, 0.1, 0.36);
    k.blk(this.body, L.skin, 0, 0.66, 0, 0.3, 0.28, 0.28); // head
    const hc = L.headColor;
    if (L.head === "helmet") k.blk(this.body, hc, 0, 0.9, 0, 0.36, 0.16, 0.38);
    else if (L.head === "beanie") {
      k.blk(this.body, hc, 0, 0.84, 0, 0.34, 0.2, 0.34);
      k.blk(this.body, "#f4f1ea", 0, 0.82, 0, 0.36, 0.07, 0.36); // fold
      k.blk(this.body, "#f4f1ea", 0, 1.04, 0, 0.12, 0.1, 0.12);  // pompom
    } else if (L.head === "cap") {
      k.blk(this.body, hc, 0, 0.88, 0, 0.34, 0.12, 0.34);
      k.blk(this.body, hc, 0, 0.88, -0.24, 0.3, 0.04, 0.16); // visor
    } else {
      k.blk(this.body, "#4a3020", 0, 0.9, 0.02, 0.32, 0.1, 0.32); // hair
      k.blk(this.body, hc, 0, 0.84, 0, 0.34, 0.07, 0.34);         // headband
    }
    if (L.glasses !== "none") k.blk(this.body, L.glasses === "mirror" ? "#3fa9e0" : "#1a1a1a", 0, 0.76, -0.15, 0.32, 0.08, 0.04);
    if (L.pack === "vest") {
      k.blk(this.body, "#2b2d33", 0, 0.08, 0.2, 0.5, 0.5, 0.12);  // hydration vest
      k.blk(this.body, "#4bb8e8", 0, 0.16, 0.27, 0.2, 0.3, 0.06); // bladder
      for (const x of [-0.17, 0.17]) k.blk(this.body, "#2b2d33", x, 0.06, -0.18, 0.1, 0.56, 0.04); // straps
    } else if (L.pack === "belt") {
      k.blk(this.body, "#2b2d33", 0, -0.04, 0, 0.66, 0.1, 0.38);
      for (const x of [-0.18, 0.18]) k.blk(this.body, "#4bb8e8", x, -0.06, 0.22, 0.12, 0.2, 0.1); // bottles
    }
    for (const s of [-1, 1]) {
      const a = new THREE.Group();
      a.position.set(s * 0.4, 0.6, 0);
      this.body.add(a);
      k.blk(a, L.jersey, 0, -0.55, 0, 0.16, 0.55, 0.16);
      k.blk(a, "#1a1a1a", 0, -0.66, 0, 0.14, 0.12, 0.14);
      k.blk(a, L.poles, 0, -1.85, 0.05, 0.05, 1.25, 0.05); // pole
      this.arms.push(a);
    }
  }

  // ── world chunks ──
  private buildChunk(c: Chunk) {
    c.geos.forEach((g) => g.dispose());
    c.geos = [];
    c.fans = [];
    c.g.clear();
    const k = this.kit;
    const p = c.g;
    const s0 = c.n * CHUNK;
    const st = stageAt(s0 + CHUNK / 2);
    const L = CHUNK;
    const bridge = st.left === "water" && st.right === "water";

    // Street
    k.blk(p, "#5b5e64", 0, -0.2, 0, ROAD_HALF * 2, 0.2, L, false);
    for (const x of [-1.1, 1.1]) for (let z = -L / 2 + 1; z < L / 2; z += 4) k.blk(p, "#e8e3d3", x, 0, z, 0.14, 0.02, 1.8, false);
    for (const sd of [-1, 1]) {
      k.blk(p, "#bdb7ab", sd * (ROAD_HALF + WALK / 2), -0.2, 0, WALK, 0.38, L, false);
      k.blk(p, "#8f8a82", sd * (ROAD_HALF + 0.08), -0.2, 0, 0.16, 0.4, L, false);
    }
    if (bridge) k.blk(p, "#6f6a63", 0, -1.1, 0, EDGE * 2, 0.9, L, false);
    if (TRAM_STAGES.includes(st.name)) for (const x of [-0.72, 0.72]) k.blk(p, "#8d9096", x, 0, 0, 0.1, 0.03, L, false);
    // Street lamp on one side
    const ls = Math.random() < 0.5 ? -1 : 1;
    const lz = rnd(-6, 6);
    k.blk(p, "#30343a", ls * (EDGE - 0.35), 0.18, lz, 0.14, 3.6, 0.14);
    k.blk(p, "#30343a", ls * (EDGE - 0.75), 3.7, lz, 0.9, 0.1, 0.12);
    k.blk(p, "#fff1b8", ls * (EDGE - 1.1), 3.5, lz, 0.4, 0.2, 0.3, false);

    if (!bridge) {
      const crowded = s0 % LAP < 140 || st.name === "Djurgården" || st.name === "Kungsträdgården";
      for (const sd of [-1, 1] as const) if (Math.random() < (crowded ? 0.9 : 0.3)) this.crowd(c, sd, crowded ? 4 + Math.floor(Math.random() * 5) : 2 + Math.floor(Math.random() * 3));
    }
    this.side(c, -1, st.left, bridge);
    this.side(c, 1, st.right, bridge);

    // Start arch
    if (s0 <= 0 && s0 + L > 0) this.gate(p, s0 + L / 2);
    c.g.position.z = this.chunkZ(c);
  }

  // Spectators on the pavement, cheering and waving flags
  private crowd(c: Chunk, sd: -1 | 1, n: number) {
    const k = this.kit;
    for (let i = 0; i < n; i++) {
      const g = new THREE.Group();
      const z = rnd(-7.5, 7.5);
      g.position.set(sd * (ROAD_HALF + 0.55 + Math.random() * 1.1), 0, z);
      g.rotation.y = sd * Math.PI / 2;
      g.scale.setScalar(Math.random() < 0.2 ? 0.7 : rnd(0.95, 1.1));
      const col = pick(["#c23b30", "#2f5d9e", "#e2b31a", "#3d7a55", "#f4f1ea", "#7a4fa0", "#d8772c"]);
      for (const x of [-0.1, 0.1]) k.blk(g, "#2b333d", x, 0, 0, 0.14, 0.7, 0.16);
      k.blk(g, col, 0, 0.7, 0, 0.46, 0.6, 0.26);
      k.blk(g, "#f1c9a5", 0, 1.3, 0, 0.26, 0.26, 0.26);
      k.blk(g, pick(["#3a2a1e", "#e6c56a", "#1a1a1a", "#8a4a2a", col]), 0, 1.54, 0, 0.3, 0.1, 0.3);
      const arms: THREE.Object3D[] = [];
      for (const x of [-1, 1]) {
        const a = new THREE.Group();
        a.position.set(x * 0.3, 1.25, 0);
        k.blk(a, col, 0, -0.55, 0, 0.12, 0.55, 0.14);
        g.add(a);
        arms.push(a);
      }
      if (Math.random() < 0.35) {
        // A Swedish flag in one raised hand
        const f = new THREE.Group();
        f.position.set(0.45, 1.0, 0.05);
        k.blk(f, "#d9d5cb", 0, 0, 0, 0.05, 1.5, 0.05, false);
        k.blk(f, "#1b5fae", 0, 1.1, 0.34, 0.04, 0.4, 0.6, false);
        k.blk(f, "#f2c818", 0, 1.26, 0.34, 0.05, 0.08, 0.6, false);
        k.blk(f, "#f2c818", 0, 1.1, 0.24, 0.05, 0.4, 0.08, false);
        g.add(f);
      }
      c.g.add(g);
      c.fans.push({ g, arms, ph: Math.random() * 6.28, z });
    }
  }

  private chunkZ(c: Chunk) {
    return -(c.n * CHUNK + CHUNK / 2 - this.dist);
  }

  private side(c: Chunk, sd: -1 | 1, type: Side, bridge: boolean) {
    const k = this.kit;
    const p = c.g;
    const L = CHUNK;
    const far = 45;
    const mid = sd * (EDGE + far / 2);

    if (type === "city") {
      k.blk(p, "#9b958b", mid, -0.2, 0, far, 0.3, L, false);
      let z = -L / 2;
      while (z < L / 2 - 0.5) {
        const w = Math.min(rnd(3.6, 6), L / 2 - z);
        this.building(c, sd, z + w / 2, w);
        z += w;
      }
    } else if (type === "park") {
      k.blk(p, GRASS, mid, -0.3, 0, far, 0.4, L, false);
      for (let i = 0, n = 1 + Math.floor(Math.random() * 3); i < n; i++) this.tree(p, sd * rnd(EDGE + 2, EDGE + 11), rnd(-6, 6), rnd(0.85, 1.25));
      if (Math.random() < 0.4) {
        const bz = rnd(-5, 5);
        k.blk(p, "#7a4f2e", sd * (EDGE + 0.6), 0.1, bz, 0.5, 0.45, 1.6);
        k.blk(p, "#7a4f2e", sd * (EDGE + 0.85), 0.55, bz, 0.12, 0.45, 1.6);
      }
    } else {
      // Quay or bridge: water beyond a granite edge or a railing
      k.blk(p, WATER, mid, -1.4, 0, far, 0.8, L, false);
      for (let i = 0; i < 3; i++) k.blk(p, "#6aa3c6", sd * rnd(EDGE + 2, EDGE + 25), -0.6, rnd(-7, 7), rnd(0.8, 2), 0.01, 0.12, false);
      if (type === "quay") {
        k.blk(p, "#a29d93", sd * (EDGE + 0.4), -1, 0, 0.8, 1.2, L, false);
        for (let z = -L / 2 + 2; z < L / 2; z += 5) k.blk(p, "#2b2e33", sd * (EDGE + 0.45), 0.2, z, 0.25, 0.35, 0.25);
        if (Math.random() < 0.5) this.boat(p, sd * rnd(EDGE + 4, EDGE + 7), rnd(-4, 4));
      } else {
        const rx = sd * (EDGE - 0.1);
        for (let z = -L / 2; z < L / 2; z += 2) k.blk(p, "#2f4a5c", rx, 0.18, z, 0.14, 1.0, 0.14);
        k.blk(p, "#2f4a5c", rx, 1.1, 0, 0.18, 0.14, L);
        if (!bridge && Math.random() < 0.3) this.boat(p, sd * rnd(EDGE + 5, EDGE + 9), rnd(-4, 4));
      }
    }
  }

  private building(c: Chunk, sd: -1 | 1, zc: number, w: number) {
    const k = this.kit;
    const p = c.g;
    const depth = 7;
    const fh = 1.8;
    const floors = 3 + Math.floor(Math.random() * 4);
    const h = floors * fh + 0.4;
    const col = pick(FACADES);
    const fx = sd * EDGE; // facade line
    const bx = fx + sd * (depth / 2);
    k.blk(p, col, bx, 0, zc, depth, h, w);
    k.blk(p, pick(ROOFS), bx, h, zc, depth + 0.1, 0.5, w + 0.02);
    if (Math.random() < 0.35) k.blk(p, "#7b4a37", bx + sd * 1.5, h + 0.5, zc + rnd(-w / 4, w / 4), 0.5, 0.8, 0.5);

    // Windows on the upper floors (a textured plane facing the street)
    const cols = Math.max(2, Math.round(w / 1.6));
    const rows = floors - 1;
    const geo = new THREE.PlaneGeometry(w - 0.2, rows * fh);
    const uv = geo.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * cols, uv.getY(i) * rows);
    c.geos.push(geo);
    const f = new THREE.Mesh(geo, k.facade(col));
    f.rotation.y = sd < 0 ? Math.PI / 2 : -Math.PI / 2;
    f.position.set(fx - sd * 0.01, fh + (rows * fh) / 2, zc);
    f.receiveShadow = true;
    p.add(f);

    // Shop front and maybe an awning
    k.blk(p, "#2c3b4c", fx - sd * 0.02, 0.18, zc, 0.06, 1.25, w - 1);
    if (Math.random() < 0.5) k.blk(p, pick(AWNINGS), fx - sd * 0.45, 1.5, zc, 0.9, 0.16, w - 0.8);
  }

  private tree(p: THREE.Object3D, x: number, z: number, s: number) {
    const k = this.kit;
    const c = pick(LEAVES);
    k.blk(p, "#5a3b24", x, 0, z, 0.4 * s, 1.4 * s, 0.4 * s);
    k.blk(p, c, x, 1.2 * s, z, 2.6 * s, 0.8 * s, 2.6 * s);
    k.blk(p, c, x, 2.0 * s, z, 1.9 * s, 0.8 * s, 1.9 * s);
    k.blk(p, c, x, 2.8 * s, z, 1.1 * s, 0.7 * s, 1.1 * s);
  }

  private boat(p: THREE.Object3D, x: number, z: number) {
    const k = this.kit;
    k.blk(p, "#f2efe8", x, -0.9, z, 2.2, 0.9, 7);
    k.blk(p, "#1f2a44", x, -0.9, z, 2.24, 0.25, 7.04, false);
    k.blk(p, "#8a5a34", x, 0, z + 0.6, 1.6, 1.0, 3.6);
    k.blk(p, "#2c3b4c", x, 0.45, z + 0.6, 1.64, 0.35, 3.2, false);
    k.blk(p, "#1a1a1a", x, 1.0, z - 0.4, 0.5, 0.9, 0.5);
  }

  private gate(p: THREE.Object3D, z: number) {
    const k = this.kit;
    const x0 = ROAD_HALF + 0.4;
    for (const sd of [-1, 1]) k.blk(p, "#2b2e33", sd * x0, 0.18, z, 0.3, 4.6, 0.3);
    k.blk(p, "#e2b31a", 0, 3.8, z, x0 * 2, 0.8, 0.2);
  }

  // ── traffic and power-ups ──
  // Models face -z; oncoming traffic is turned round to face the player
  private vehicle(kind: VehicleKind): THREE.Group {
    const k = this.kit;
    const g = new THREE.Group();
    const wheels = (xs: number[], zs: number[]) => {
      for (const x of xs) for (const z of zs) k.blk(g, "#17191c", x, 0, z, 0.3, 0.6, 0.62);
    };
    const lights = (w: number, y: number, len: number) => {
      for (const x of [-w, w]) {
        k.blk(g, "#d23a2f", x, y, len / 2 + 0.01, 0.35, 0.18, 0.04, false);
        k.blk(g, "#fff3c4", x, y, -len / 2 - 0.01, 0.35, 0.18, 0.04, false);
      }
    };
    const rider = (y: number, jersey: string, lean: number) => {
      const b = new THREE.Group();
      b.position.y = y;
      b.rotation.x = -lean;
      g.add(b);
      g.userData.rider = b;
      k.blk(b, jersey, 0, 0, 0, 0.45, 0.6, 0.3);
      k.blk(b, "#f1c9a5", 0, 0.6, 0, 0.26, 0.26, 0.26);
      k.blk(b, "#1a1a1a", 0, 0.82, 0, 0.3, 0.12, 0.32);
    };

    switch (kind) {
      case "car":
      case "taxi": {
        const col = kind === "taxi" ? "#f1c232" : pick(CARS);
        k.blk(g, col, 0, 0.3, 0, 1.7, 0.65, 3.8);
        k.blk(g, "#2b333d", 0, 0.95, 0.2, 1.5, 0.55, 2.0);
        k.blk(g, col, 0, 1.48, 0.2, 1.52, 0.1, 1.9);
        wheels([-0.78, 0.78], [-1.2, 1.2]);
        lights(0.6, 0.6, 3.8);
        if (kind === "taxi") k.blk(g, "#f6f1df", 0, 1.58, 0.2, 0.6, 0.22, 0.35);
        break;
      }
      case "van":
        k.blk(g, "#eeeae2", 0, 0.3, 0.3, 1.9, 2.0, 4.2);
        k.blk(g, "#eeeae2", 0, 0.3, -2.0, 1.9, 1.2, 0.8);
        k.blk(g, "#2b333d", 0, 1.5, -1.95, 1.8, 0.7, 0.7);
        k.blk(g, pick(["#2f5d9e", "#c23b30", "#3d7a55"]), 0, 0.9, 0.3, 1.94, 0.3, 4.0, false);
        wheels([-0.85, 0.85], [-1.6, 1.5]);
        lights(0.7, 0.55, 4.8);
        break;
      case "bus":
        k.blk(g, "#c8302c", 0, 0.35, 0, 2.3, 2.6, 10.5);
        k.blk(g, "#2b333d", 0, 1.4, 0, 2.34, 0.9, 9.6, false);
        k.blk(g, "#2b333d", 0, 1.4, -5.26, 1.9, 0.9, 0.04, false);
        k.blk(g, "#9a9ea6", 0, 2.95, 1, 1.6, 0.35, 3);
        wheels([-1.0, 1.0], [-3.6, 3.4]);
        lights(0.85, 0.6, 10.5);
        break;
      case "bike":
        for (const z of [-0.55, 0.55]) k.blk(g, "#1a1a1a", 0, 0, z, 0.08, 0.72, 0.72);
        k.blk(g, pick(CARS), 0, 0.5, 0, 0.08, 0.08, 1.0);
        k.blk(g, "#23324f", 0, 0.45, 0.1, 0.25, 0.55, 0.22);
        rider(1.0, pick(JERSEYS), 0.5);
        break;
      case "scooter":
        k.blk(g, "#2a9d8f", 0, 0.08, 0, 0.25, 0.08, 0.95);
        k.blk(g, "#2a9d8f", 0, 0.1, -0.42, 0.06, 1.05, 0.06);
        k.blk(g, "#1a1a1a", 0, 1.12, -0.42, 0.5, 0.05, 0.06);
        k.blk(g, "#3b3d42", 0, 0.16, 0, 0.3, 0.75, 0.2);
        rider(0.9, pick(JERSEYS), 0.1);
        break;
      case "sport":
        k.blk(g, "#d62828", 0, 0.25, 0, 1.8, 0.5, 4.2);
        k.blk(g, "#2b333d", 0, 0.75, 0.4, 1.4, 0.4, 1.6);
        k.blk(g, "#d62828", 0, 1.15, 0.4, 1.42, 0.08, 1.4);
        k.blk(g, "#f4f1ea", 0, 0.75, -1.2, 0.3, 0.02, 1.7, false);
        k.blk(g, "#1a1a1a", 0, 0.95, 1.95, 1.7, 0.08, 0.35);
        for (const x of [-0.7, 0.7]) k.blk(g, "#1a1a1a", x, 0.75, 1.95, 0.08, 0.2, 0.08);
        wheels([-0.82, 0.82], [-1.3, 1.3]);
        lights(0.6, 0.45, 4.2);
        break;
      case "tram": // two cars, cream with a blue band
        for (const zc of [-5.6, 5.6]) {
          k.blk(g, "#e9dfc6", 0, 0.3, zc, 2.4, 2.7, 10.6);
          k.blk(g, "#2f5d9e", 0, 0.3, zc, 2.44, 0.9, 10.64, false);
          k.blk(g, "#2b333d", 0, 1.45, zc, 2.44, 0.9, 9.8, false);
          k.blk(g, "#9a9ea6", 0, 3.0, zc, 1.6, 0.25, 8);
        }
        k.blk(g, "#3b3d42", 0, 0.4, 0, 2.0, 2.4, 0.8);
        k.blk(g, "#2b333d", 0, 1.45, -10.92, 1.9, 0.9, 0.04, false);
        k.blk(g, "#30343a", 0, 3.25, -5.6, 0.1, 0.8, 1.2);
        for (const z of [-9, -2.5, 2.5, 9]) k.blk(g, "#17191c", 0, 0, z, 2.0, 0.4, 1.6);
        lights(0.85, 0.6, 22);
        break;
    }
    g.rotation.y = Math.PI;
    return g;
  }

  private powerModel(kind: PowerKind): THREE.Group {
    const k = this.kit;
    const g = new THREE.Group();
    switch (kind) {
      case "bun":
        k.blk(g, "#b8763a", 0, 0.5, 0, 0.75, 0.3, 0.75);
        k.blk(g, "#d89a55", 0, 0.8, 0, 0.45, 0.14, 0.45);
        for (const [x, z] of [[0.2, 0.1], [-0.15, -0.2], [0.05, -0.05], [-0.22, 0.2]]) k.blk(g, "#fffaf0", x, 0.82, z, 0.1, 0.08, 0.1, false);
        break;
      case "soup": // blueberry soup in a yellow paper cup, as at Vasaloppet
        k.blk(g, "#e2b31a", 0, 0.2, 0, 0.36, 0.4, 0.36); // cup base
        k.blk(g, "#e2b31a", 0, 0.58, 0, 0.5, 0.36, 0.5); // cup top
        k.blk(g, "#f4d44a", 0, 0.72, 0, 0.54, 0.08, 0.54, false); // rolled rim
        k.blk(g, "#3b2a73", 0, 0.78, 0, 0.42, 0.05, 0.42, false); // soup
        for (const [x, z] of [[-0.1, -0.08], [0.1, 0.07]]) k.blk(g, "#6d8fe0", x, 0.82, z, 0.08, 0.05, 0.08, false); // berries
        k.blk(g, "#2d6fb5", 0, 0.42, 0.26, 0.2, 0.14, 0.02, false); // print
        break;
      case "shield": // energy gel: a black sachet with a tear-off tab
        k.blk(g, "#17181b", 0, 0.3, 0, 0.6, 0.85, 0.12);
        k.blk(g, "#17181b", 0.12, 1.15, 0, 0.3, 0.14, 0.1);
        k.blk(g, "#ff6a1a", 0, 0.62, 0, 0.62, 0.12, 0.13, false);
        k.blk(g, "#f4f1ea", 0, 0.82, 0, 0.36, 0.08, 0.13, false);
        break;
      case "slow": // red light
        k.blk(g, "#2b2e33", 0, 0.4, 0, 0.42, 1.05, 0.36);
        for (const z of [-0.19, 0.19]) {
          k.blk(g, "#ff3b30", 0, 1.15, z, 0.24, 0.2, 0.02, false);
          k.blk(g, "#5a4a1a", 0, 0.85, z, 0.24, 0.2, 0.02, false);
          k.blk(g, "#1d4a2a", 0, 0.55, z, 0.24, 0.2, 0.02, false);
        }
        break;
    }
    return g;
  }

  // Difficulty ramps up for the whole run
  private skierTarget() {
    return Math.min(TOP_SPEED, 16 + 12 * (1 - Math.exp(-this.time / 40)) + 0.05 * this.time + 0.6 * this.buns);
  }
  private trafficSpeed() {
    const v = 8 + 9 * (1 - Math.exp(-this.time / 50)) + 0.04 * this.time + 0.2 * this.buns;
    return this.slow > 0 ? v * 0.25 : v;
  }

  private signalSecs() {
    return Math.max(SIGNAL_MIN, SIGNAL_SECS - this.time * 0.002);
  }

  // A row of 1–2 vehicles, always leaving at least one lane free. All traffic
  // drives at the same speed, so rows keep their spacing.
  private spawnRow() {
    const t = this.time;
    const rel = Math.max(this.speed, 11) + this.trafficSpeed();
    const tGap = Math.max(0.55, 1.4 - t * 0.01) + Math.random() * Math.max(0.25, 0.9 - t * 0.007);
    let z = this.frontZ - Math.max(9, rel * tGap);
    // Skip rows that would reach a crossing while people are on it, before
    // the player gets there: no one has to stop, the player only dodges people
    const c = this.crossing;
    if (c && c.state !== "done" && c.g.position.z < -2) {
      const v = this.trafficSpeed();
      const toPlayer = -c.g.position.z / Math.max(this.speed, 11);
      const reach = (c.g.position.z - CROSS_D / 2 - z) / v;
      if (reach > toPlayer - CROSS_SECS - 1.5 && reach < toPlayer + 0.5)
        z = c.g.position.z - CROSS_D / 2 - v * (toPlayer + 0.5) - 1;
    }
    // Roadworks: nothing may reach them until the player has been through and
    // they are behind the camera, however far off they still are
    const s = this.site;
    if (s) {
      const zf = s.g.position.z - SITE_LEN / 2;
      if (zf < 8) {
        const v = this.trafficSpeed();
        const need = ((8 - zf) / Math.max(this.speed, 11)) * 1.2;
        if ((zf - z) / v < need) z = zf - v * need - 1;
      }
    }
    const n = Math.random() < Math.min(0.75, 0.3 + t * 0.008) ? 2 : 1;
    const lanes = [0, 1, 2].sort(() => Math.random() - 0.5);
    // A row on its own may instead be a tram (middle lane) or a speeding car
    let special: VehicleKind | null = null;
    if (n === 1) {
      if (TRAM_STAGES.includes(stageAt(this.dist - z).name) && Math.random() < 0.2) special = "tram";
      else if (t > 15 && Math.random() < Math.min(0.2, 0.05 + t * 0.001)) special = "sport";
    }
    if (special === "tram") lanes.sort((a, b) => Math.abs(a - 1) - Math.abs(b - 1));
    let maxLen = 0;
    for (let i = 0; i < n; i++) {
      const x = Math.random();
      const kind: VehicleKind = special ??
        (x < 0.36 ? "car" : x < 0.46 ? "taxi" : x < 0.6 ? "van" : x < 0.7 ? "bus" : x < 0.85 ? "bike" : "scooter");
      const steady = kind === "bus" || kind === "tram" || kind === "sport";
      const { len, w } = SIZE[kind];
      const g = this.vehicle(kind);
      g.position.set(LANES[lanes[i]], 0, z - len / 2);
      this.scene.add(g);
      const soft = kind === "bike" || kind === "scooter";
      const blink: Record<number, THREE.Mesh[]> = { [-1]: [], 1: [] };
      // Bright amber lamps with a soft glow around them; the model is turned
      // round, so world side s is local x = -s
      const lamp = (s: number, x: number, y: number, z: number, sz: number) => {
        const core = new THREE.Mesh(this.kit.box, this.kit.glow("#ffb000"));
        core.scale.set(sz, sz, sz);
        const halo = new THREE.Mesh(this.kit.box, this.kit.glow("#ffd34d", 0.6));
        halo.scale.set(sz * 3, sz * 3, sz * 3);
        for (const m of [core, halo]) {
          m.position.set(x, y, z);
          m.visible = false;
          g.add(m);
          blink[s].push(m);
        }
      };
      if (soft) {
        for (const s of [-1, 1]) {
          lamp(s, -s * 0.42, 1.15, -0.2, 0.3);
          lamp(s, -s * 0.42, 1.15, 0.2, 0.3);
        }
      } else if (!steady) {
        for (const s of [-1, 1]) {
          lamp(s, -s * (w / 2 + 0.04), 0.75, -len / 2 + 0.3, 0.5);
          lamp(s, -s * (w / 2 + 0.04), 0.75, len / 2 - 0.3, 0.5);
          lamp(s, -s * (w / 2 - 0.3), 0.75, -len / 2 - 0.04, 0.5);
          lamp(s, -s * (w / 2 - 0.3), 0.75, len / 2 + 0.04, 0.5);
        }
      }
      // Buses, trams and speeders keep their lane; the share of lane changers grows with time
      const swerves = !steady && Math.random() < Math.min(0.5, 0.15 + t * 0.005);
      // Late enough to be on screen, early enough to finish with time to react
      const near = Math.min(58, rel * (this.signalSecs() + 2.2 / SWERVE_SPEED + 0.9));
      const swerveZ = swerves ? -rnd(near, Math.max(near + 6, 60)) : null;
      const boost = kind === "sport" ? SPORT_BOOST : 1;
      const cue = kind === "sport" ? "rev" : kind === "tram" ? "ding" : null;
      this.traffic.push({ g, len, w, soft, hit: false, v: this.trafficSpeed() * boost, lane: lanes[i], swerveZ, signal: 0, dir: 0, blink, boost, cue, rider: soft ? (g.userData.rider as THREE.Object3D) : null });
      maxLen = Math.max(maxLen, len);
    }
    this.frontZ = z - maxLen;
  }

  private spawnPower() {
    const r = Math.random();
    const kind: PowerKind = r < 0.8 ? "bun" : r < 0.88 ? "soup" : r < 0.94 ? "shield" : "slow";
    const g = this.powerModel(kind);
    g.position.set(LANES[Math.floor(Math.random() * 3)], 0, -AHEAD);
    this.scene.add(g);
    this.powers.push({ g, kind });
  }

  private spawnSite() {
    const k = this.kit;
    const g = new THREE.Group();
    const L = SITE_LEN / 2;
    g.position.z = this.frontZ - 4 - L;
    this.frontZ = g.position.z - L - 2;
    const lanes = [0, 1, 2].sort(() => Math.random() - 0.5);
    const ramp = lanes[0];
    const blocked = lanes.slice(1, Math.random() < 0.6 ? 2 : 3);
    // Torn-up asphalt across the whole road
    k.blk(g, "#7a7468", 0, 0, 0, ROAD_HALF * 2, 0.02, SITE_LEN, false);
    for (const l of [0, 1, 2]) {
      const x = LANES[l];
      if (l === ramp) {
        // Steps up to a ramp, then a trench to fly over
        for (let i = 0; i < RAMP_LEN; i++) {
          const top = RAMP_H * (1 - (RAMP_LEN - 0.5 - i) / RAMP_LEN);
          k.blk(g, i % 2 ? "#b8763a" : "#d89a55", x, 0, RAMP_LEN - 0.5 - i, 1.9, Math.max(0.05, top), 1);
          // Pearl sugar on top
          for (const dx of [-0.5, 0.1, 0.55]) k.blk(g, "#fffaf0", x + dx + (i % 2) * 0.15, Math.max(0.05, top), RAMP_LEN - 0.5 - i + (dx > 0 ? 0.2 : -0.2), 0.14, 0.05, 0.14, false);
        }
        k.blk(g, "#241c17", x, 0, -L / 2, 1.9, 0.04, L, false);
        for (const sd of [-1, 1]) k.blk(g, "#e2b31a", x + sd * 1.0, 0, -L / 2, 0.08, 0.3, L, false);
      } else if (blocked.includes(l)) {
        // Fenced-off dig: boards at the near end, a dirt heap, and some machinery
        for (const sd of [-0.6, 0.6]) k.blk(g, "#f4f1ea", x + sd, 0.4, L - 0.3, 0.7, 0.35, 0.12);
        k.blk(g, "#e2602a", x, 0.4, L - 0.3, 0.5, 0.35, 0.13, false);
        k.blk(g, "#30343a", x - 1.0, 0, L - 0.3, 0.1, 0.9, 0.1);
        k.blk(g, "#30343a", x + 1.0, 0, L - 0.3, 0.1, 0.9, 0.1);
        k.blk(g, "#7a5a3a", x, 0, 0, 1.9, 0.9, SITE_LEN - 1.2);
        k.blk(g, "#8b6a45", x, 0.9, -1.5, 1.2, 0.5, 4);
        if (Math.random() < 0.6) {
          // Excavator
          k.blk(g, "#17191c", x, 1.4, 1.5, 1.4, 0.45, 2.2);
          k.blk(g, "#e2b31a", x, 1.85, 1.5, 1.2, 0.6, 1.3);
          k.blk(g, "#2b333d", x, 2.45, 1.2, 0.8, 0.6, 0.8);
          k.blk(g, "#e2b31a", x, 2.0, -0.3, 0.2, 0.2, 2.2);
          k.blk(g, "#e2b31a", x, 1.2, -1.5, 0.2, 1.0, 0.2);
        } else {
          k.blk(g, "#2f6aa8", x, 0.9, 1.5, 1.7, 1.5, 3.2);
          k.blk(g, "#1f4a78", x, 0.9, 1.5, 1.74, 0.2, 3.24, false);
        }
      } else {
        // Open lane squeezed past the works: just cones
        for (const z of [L - 1, 1.5, -3])
          for (const sd of [-1, 1]) {
            k.blk(g, "#e2602a", x + sd * 0.9, 0, z, 0.3, 0.55, 0.3);
            k.blk(g, "#f4f1ea", x + sd * 0.9, 0.3, z, 0.32, 0.1, 0.32, false);
          }
      }
    }
    // Warning signs on both kerbs ahead of the works
    for (const sd of [-1, 1])
      for (const z of [L + 4, L + 14]) {
        k.blk(g, "#30343a", sd * (ROAD_HALF + 0.4), 0, z, 0.1, 2.0, 0.1);
        const sign = k.blk(g, "#e2b31a", sd * (ROAD_HALF + 0.4), 2.0, z, 0.9, 0.9, 0.06);
        sign.rotation.z = Math.PI / 4;
        k.blk(g, "#1a1a1a", sd * (ROAD_HALF + 0.4), 2.3, z + 0.04, 0.12, 0.45, 0.02, false);
      }
    this.scene.add(g);
    this.site = { g, ramp, blocked };
    // Buns up the ramp and hanging in the air over the trench, where the jump takes you
    [[4.5, 0.6], [2.5, 1.0], [-1, 2.0], [-3.2, 2.5], [-5.4, 2.5]].forEach(([z, y]) => {
      const b = this.powerModel("bun");
      b.position.set(LANES[ramp], y - 0.5, g.position.z + z);
      this.scene.add(b);
      this.powers.push({ g: b, kind: "bun" });
    });
  }

  private moveSite(dt: number) {
    const s = this.site;
    if (!s) return;
    s.g.position.z += this.speed * dt;
    if (s.g.position.z - SITE_LEN / 2 - 14 > BACK) {
      this.scene.remove(s.g);
      this.site = null;
    }
  }

  // Up the ramp, off the end and through the air, over everything on the road
  private updateJump(dt: number) {
    if (this.airborne) {
      this.jumpV -= GRAV * dt;
      this.jumpY += this.jumpV * dt;
      if (this.jumpY <= 0) {
        this.jumpY = 0;
        this.airborne = false;
        this.inv = Math.max(this.inv, 0.7);
        sfx.play("land");
      }
      return;
    }
    const s = this.site;
    const l = s ? -s.g.position.z : -1; // how far along the works the player is
    if (s && l >= -0.3 && l <= RAMP_LEN && Math.abs(this.x - LANES[s.ramp]) < 0.8) {
      this.onRamp = true;
      this.jumpY = RAMP_H * (1 - Math.max(0, l) / RAMP_LEN);
      if (l < 0.05) {
        this.onRamp = false;
        this.airborne = true;
        this.jumpV = Math.min(10, Math.max(8, this.speed * 0.35));
        sfx.play("launch");
      }
    } else if (this.onRamp) {
      this.onRamp = false;
      this.jumpY = 0;
    }
  }

  private launchRider(t: Traffic) {
    const b = t.rider;
    if (!b) return;
    t.rider = null;
    this.scene.attach(b);
    const side = Math.sign(t.g.position.x - this.x) || (Math.random() < 0.5 ? -1 : 1);
    t.g.userData.fall = side;
    this.flyers.push({
      g: b,
      vx: side * rnd(2.5, 4.5),
      vy: rnd(7, 10),
      vz: -rnd(0, 3),
      spin: new THREE.Vector3(rnd(-9, 9), 0, side * rnd(5, 9)),
      age: 0,
    });
    sfx.play("whack");
  }

  private updateFlyers(dt: number) {
    const sp = this.phase === "play" ? this.speed : 0;
    this.flyers = this.flyers.filter((f) => {
      f.age += dt;
      f.vy -= GRAV * dt;
      const p = f.g.position;
      p.x += f.vx * dt;
      p.y += f.vy * dt;
      p.z += (sp * 0.8 + f.vz) * dt;
      if (p.y < 0.1 && f.vy < 0) {
        p.y = 0.1;
        const hard = f.vy < -4;
        f.vy = hard ? -f.vy * 0.3 : 0;
        f.vx *= hard ? 0.5 : 0.9;
        f.spin.multiplyScalar(hard ? 0.5 : 0.8);
      }
      f.g.rotation.x += f.spin.x * dt;
      f.g.rotation.z += f.spin.z * dt;
      if (f.age > 2.5 || p.z > BACK) {
        this.scene.remove(f.g);
        return false;
      }
      return true;
    });
  }

  private spawnCrossing() {
    const k = this.kit;
    const g = new THREE.Group();
    // Beyond the last row of traffic; spawnRow keeps later rows clear of it
    g.position.z = this.frontZ - 4;
    for (let x = -ROAD_HALF + 0.5; x < ROAD_HALF; x += 1) k.blk(g, "#f1ede2", x, 0, 0, 0.5, 0.025, CROSS_D, false);
    // Pole with the blue crossing sign on each side
    for (const s of [-1, 1]) {
      k.blk(g, "#30343a", s * (ROAD_HALF + 0.4), 0.18, -s * (CROSS_D / 2 + 0.3), 0.1, 2.4, 0.1);
      k.blk(g, "#2a63b8", s * (ROAD_HALF + 0.4), 2.2, -s * (CROSS_D / 2 + 0.3), 0.08, 0.6, 0.6);
      k.blk(g, "#f4f1ea", s * (ROAD_HALF + 0.35), 2.35, -s * (CROSS_D / 2 + 0.3), 0.06, 0.3, 0.3, false);
    }
    // 2 people 2.4 m apart or 3 people 3.3 m apart, all heading the same way:
    // either way no more than two lanes are blocked at once
    const n = Math.random() < 0.5 ? 2 : 3;
    const gap = n === 2 ? 2.4 : 3.3;
    const dir = pick([-1, 1]);
    const lead = rnd(0, 1.4);
    const peds: Ped[] = [];
    for (let i = 0; i < n; i++) {
      const p = new THREE.Group();
      p.position.set(-dir * (ROAD_HALF + 0.6 + i * 0.5), 0.18, CROSS_D / 2 - 0.5 - i * 0.9);
      p.rotation.y = dir > 0 ? -Math.PI / 2 : Math.PI / 2;
      k.blk(p, "#2b333d", -0.1, 0, 0, 0.14, 0.75, 0.16);
      k.blk(p, "#2b333d", 0.1, 0, 0, 0.14, 0.75, 0.16);
      k.blk(p, pick(["#7a4fa0", "#c23b30", "#3d7a55", "#d8772c", "#2f5d9e", "#a7bda4"]), 0, 0.75, 0, 0.48, 0.65, 0.28);
      k.blk(p, "#f1c9a5", 0, 1.4, 0, 0.26, 0.26, 0.26);
      k.blk(p, pick(["#3a2a1e", "#e6c56a", "#1a1a1a", "#8a4a2a"]), 0, 1.6, 0.02, 0.3, 0.1, 0.3);
      g.add(p);
      // Each one steps off the kerb when the one in front is `gap` ahead
      peds.push({ g: p, dir, wait: lead + (i * gap - i * 0.5) / PED_SPEED });
    }
    this.scene.add(g);
    this.crossing = { g, peds, state: "wait" };
  }

  private moveCrossing(dt: number) {
    const c = this.crossing;
    if (!c) return;
    c.g.position.z += this.speed * dt;
    const cz = c.g.position.z;
    if (cz > BACK) {
      this.scene.remove(c.g);
      this.crossing = null;
      return;
    }
    // Becomes active a couple of seconds out, but never further away than the
    // screen shows
    const show = Math.min(58, Math.max(30, this.speed * CROSS_SECS));
    if (c.state === "wait" && cz > -show) c.state = "stop";
    // Swedish crossing signal: a slow tick while waiting, fast while walking
    if (c.state === "stop" || c.state === "walk") {
      this.tickT -= dt;
      if (this.tickT <= 0) {
        sfx.play("tick");
        this.tickT = c.state === "walk" ? 0.16 : 0.8;
      }
    }
    // People step out once nothing is driving over the stripes
    if (c.state === "stop" && !this.traffic.some((t) => Math.abs(t.g.position.z - cz) < t.len / 2 + CROSS_D / 2 + 0.5))
      c.state = "walk";
    if (c.state !== "walk") return;
    let across = true;
    for (const p of c.peds) {
      if (p.dir * p.g.position.x > ROAD_HALF + 0.6) continue;
      across = false;
      if ((p.wait -= dt) > 0) continue;
      p.g.position.x += p.dir * PED_SPEED * dt;
      p.g.position.y = 0.18 + Math.abs(Math.sin(p.g.position.x * 3)) * 0.08;
    }
    if (across) c.state = "done";
  }

  private crossBusy() {
    const c = this.crossing;
    return !!c && (c.state === "stop" || c.state === "walk");
  }

  private moveThings(dt: number) {
    const base = this.trafficSpeed();
    this.frontZ += (this.speed + base) * dt;
    this.moveCrossing(dt);
    this.moveSite(dt);
    // Nearest first, so each vehicle can follow the one in front of it
    this.traffic.sort((a, b) => b.g.position.z - a.g.position.z);
    // Traffic waits for people on the crossing, until the player is past it
    const stopLine = this.crossBusy() && this.crossing!.g.position.z < -2 ? this.crossing!.g.position.z - CROSS_D / 2 - 1 : null;
    for (const t of this.traffic) {
      const front = t.g.position.z + t.len / 2;
      let v = base * t.boost;
      if (stopLine !== null && front < stopLine + 0.5) v = Math.min(v, Math.max(0, (stopLine - front) * 1.5));
      for (const o of this.traffic) {
        if (o === t || o.lane !== t.lane || o.g.position.z <= t.g.position.z) continue;
        const gap = o.g.position.z - o.len / 2 - front;
        if (gap < 6) v = Math.min(v, o.v + Math.max(0, gap - 2) * 1.5);
      }
      if (t.boost > 1) {
        // A speeder never squeezes past a row that fills both other lanes,
        // so there is always a way through
        const ahead = this.traffic.filter((o) => o.lane !== t.lane && o.g.position.z > t.g.position.z && o.g.position.z - o.len / 2 - front < 8);
        if (new Set(ahead.map((o) => o.lane)).size >= 2)
          for (const o of ahead) v = Math.min(v, o.v + Math.max(0, o.g.position.z - o.len / 2 - front - 2) * 1.5);
      }
      t.v = v < t.v ? v : Math.min(v, t.v + 6 * dt);
      const z0 = t.g.position.z;
      t.g.position.z += (this.speed + t.v) * dt;
      if (t.cue && t.g.position.z > -50) {
        sfx.play(t.cue);
        t.cue = null;
      }
      // The odd honk from a car passing close by
      if (z0 < 0 && t.g.position.z >= 0 && !t.soft && !t.hit && Math.abs(t.g.position.x - this.x) < 2.8 && this.hornT <= 0) {
        this.hornT = 3;
        if (Math.random() < 0.4) sfx.play("horn");
      }
    }
    this.traffic = this.traffic.filter((t) => {
      if (t.g.position.z - t.len / 2 > BACK) {
        this.scene.remove(t.g);
        return false;
      }
      this.steerTraffic(t, dt);
      return true;
    });
    this.powers = this.powers.filter((p) => {
      p.g.position.z += this.speed * dt;
      p.g.rotation.y += dt * 2.5;
      if (p.g.position.z > BACK) {
        this.scene.remove(p.g);
        return false;
      }
      return true;
    });
  }

  private steerTraffic(t: Traffic, dt: number) {
    const p = t.g.position;
    // A bike that has lost its rider keels over
    if (t.hit && t.soft) {
      const side = (t.g.userData.fall as number) || 1;
      t.g.rotation.z += (side * 1.45 - t.g.rotation.z) * Math.min(1, dt * 8);
      return;
    }
    if (t.swerveZ !== null && p.z > t.swerveZ && !t.hit) {
      t.swerveZ = null;
      // Only into a neighbouring lane with nothing alongside
      const free = (l: number) =>
        l >= 0 && l <= 2 &&
        !this.traffic.some((o) => o !== t && o.lane === l && Math.abs(o.g.position.z - p.z) < (o.len + t.len) / 2 + 3);
      const dirs = [-1, 1].filter((d) => free(t.lane + d));
      if (dirs.length) {
        t.dir = pick(dirs);
        t.lane += t.dir; // claim the lane now so no one else takes it
        t.signal = this.signalSecs();
      }
    }
    if (t.signal > 0) {
      t.signal -= dt;
      // Starts lit and flashes fast, so it reads straight away
      const on = t.signal > 0 && ((this.signalSecs() - t.signal) * 5) % 1 < 0.7;
      t.blink[t.dir].forEach((m) => (m.visible = on));
      if (t.soft) t.g.rotation.z = t.signal > 0 ? -t.dir * 0.45 : 0;
      return;
    }
    const dx = LANES[t.lane] - p.x;
    if (dx === 0) return;
    // Quicker across at high speed, so a lane change doesn't drag on for metres
    const sw = Math.max(SWERVE_SPEED, (this.speed + t.v) * 0.11);
    const step = Math.sign(dx) * Math.min(Math.abs(dx), sw * dt);
    p.x += step;
    // Nose turned slightly towards the new lane while crossing
    t.g.rotation.y = Math.PI + (p.x === LANES[t.lane] ? 0 : Math.sign(dx) * 0.22);
  }

  private collide() {
    const near = (x: number, z: number, len: number, w: number) =>
      Math.abs(this.x - x) < w / 2 + 0.3 && z - len / 2 < 0.8 && z + len / 2 > -0.8;

    this.powers = this.powers.filter((p) => {
      if (!near(p.g.position.x, p.g.position.z, 0.9, 1.0)) return true;
      sfx.play(p.kind);
      if (p.kind === "bun") this.buns++;
      else if (p.kind === "soup") this.buns += 2; // worth two buns
      else if (p.kind === "shield") this.shield = POWER_SECS;
      else this.slow = POWER_SECS;
      this.scene.remove(p.g);
      return false;
    });

    if (this.shield > 0 || this.inv > 0 || this.airborne) return;
    const s = this.site;
    if (s && s.g.position.z + SITE_LEN / 2 > -0.8 && s.g.position.z - SITE_LEN / 2 < 0.8)
      for (const l of s.blocked)
        if (Math.abs(this.x - LANES[l]) < 1.3) {
          this.crash(false);
          return;
        }
    for (const t of this.traffic) {
      if (t.hit || !near(t.g.position.x, t.g.position.z, t.len, t.w)) continue;
      t.hit = true;
      // Side hit: the vehicle's near end had already gone past the player's
      // front this frame, so the player ran into its flank (or it into theirs)
      const side = t.g.position.z + t.len / 2 > -0.8 + (this.speed + t.v) * this.lastDt + 0.2;
      if (t.soft) {
        this.launchRider(t);
        if (side) continue; // knocked off from the side: costs nothing
        this.crash(true, "bell");
      } else this.crash(side, "crash"); // flank of a car: one strike; head-on: over
      return;
    }
    // Running into someone on the crossing is as bad as hitting a car
    const c = this.crossing;
    if (c && c.state === "walk")
      for (const p of c.peds)
        if (near(p.g.position.x, c.g.position.z + p.g.position.z, 0.5, 0.5)) {
          this.crash(false);
          return;
        }
  }

  private crash(soft: boolean, sound: SfxName = soft ? "bell" : "crash") {
    if (soft && ++this.strikes < SOFT_STRIKES) {
      sfx.play(sound);
      return this.hurt(1);
    }
    sfx.play(sound);
    this.strikes = 0;
    this.gameOver();
  }

  private hurt(secs: number) {
    this.inv = secs;
    this.stumble = 0.5;
    this.speed = 6;
  }

  // ── loop ──
  private loop = (t: number) => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(0.05, (t - (this.last || t)) / 1000);
    this.last = t;
    this.update(dt);
    this.renderer.render(this.scene, this.cam);
  };

  private update(dt: number) {
    this.lastDt = dt;
    if (this.phase === "count") {
      this.countT -= dt;
      const n = Math.ceil(this.countT);
      if (n !== this.lastCount) {
        this.lastCount = n;
        sfx.play(n > 0 ? "count" : "go");
      }
      if (this.countT <= 0) this.phase = "play";
    } else if (this.phase === "play") {
      this.time += dt;
      this.hornT -= dt;
      this.speed = Math.min(this.skierTarget(), this.speed + 6 * dt);
      this.dist += this.speed * dt;
      this.inv = Math.max(0, this.inv - dt);
      this.shield = Math.max(0, this.shield - dt);
      this.slow = Math.max(0, this.slow - dt);
      this.moveThings(dt);
      this.updateJump(dt);
      while (this.frontZ > -AHEAD) this.spawnRow();
      // Now and then a horn far off
      if ((this.ambT -= dt) <= 0) {
        this.ambT = rnd(3, 7);
        if (Math.random() < 0.5) sfx.play("horn");
      }
      if (this.dist >= this.nextPower) {
        this.spawnPower();
        this.nextPower += rnd(32, 70);
      }
      if (!this.crossing && !this.site && this.dist >= this.nextSite) {
        this.spawnSite();
        this.nextSite = this.dist + rnd(320, 520);
      }
      if (!this.crossing && !this.site && this.dist >= this.nextCross) {
        this.spawnCrossing();
        this.nextCross = this.dist + rnd(170, 300);
      }
      this.collide();
    }

    this.updateFlyers(dt);
    this.anim += dt;
    for (const c of this.chunks) {
      for (const f of c.fans) {
        const w = Math.sin(this.anim * 7 + f.ph);
        f.g.position.y = Math.abs(w) * 0.12;
        f.arms[0].rotation.z = -2.5 - 0.5 * w;
        f.arms[1].rotation.z = 2.5 + 0.5 * w;
      }
    }
    for (const c of this.chunks) {
      if (this.chunkZ(c) - CHUNK / 2 > BACK) {
        c.n += NCH;
        this.buildChunk(c);
      }
      c.g.position.z = this.chunkZ(c);
    }

    if (this.phase === "over") {
      // Tumble over sideways
      this.fall = Math.min(1, this.fall + dt * 2.5);
      this.skier.rotation.z = -1.45 * this.fall;
      this.skier.position.y = Math.sin(this.fall * Math.PI) * 0.6;
      this.body.rotation.x = -0.6;
    } else {
      // Glide between lanes, pole in rhythm with the speed
      const tx = LANES[this.lane];
      this.x += (tx - this.x) * Math.min(1, dt * 14);
      this.skier.position.x = this.x;
      this.skier.position.y = this.jumpY;
      this.skier.rotation.z = (this.x - tx) * 0.12;
      const stroke = Math.floor(this.pole / (Math.PI * 2) + 0.25);
      this.pole += dt * (this.speed > 0.5 ? 2.2 + this.speed * 0.16 : 1.2);
      // Pole tips hit the asphalt once per stroke, at the forward swing
      if (this.phase === "play" && this.speed > 2 && Math.floor(this.pole / (Math.PI * 2) + 0.25) !== stroke) sfx.play("pole");
      const sw = Math.sin(this.pole);
      const moving = this.speed > 0.5;
      this.arms.forEach((a) => (a.rotation.x = moving ? 0.25 + 0.75 * sw : 0.15 + 0.05 * sw));
      this.body.rotation.x = moving ? -0.25 - 0.25 * (0.5 - 0.5 * sw) : -0.05;
      this.stumble = Math.max(0, this.stumble - dt);
      // In the gear close-up the skier turns slowly so every side can be seen
      this.spin = this.closeUp && this.phase === "ready" ? this.spin + dt * 0.9 : 0;
      this.skier.rotation.y = this.stumble > 0 ? Math.sin(this.stumble * 40) * 0.25 : this.spin;
      this.skier.visible = this.inv <= 0 || Math.floor(this.inv * 10) % 2 === 0;
    }
    this.bubble.visible = this.shield > 0 && (this.shield > 1 || Math.floor(this.shield * 8) % 2 === 0);

    this.hudT -= dt;
    if (this.hudT <= 0) {
      this.hudT = 0.1;
      this.emitHud();
    }
  }

  // Only buns count; distance is just for show
  private score() {
    return this.buns;
  }

  private emitHud() {
    this.cb.onHud({
      score: this.score(),
      dist: this.dist,
      buns: this.buns,
      strikes: this.strikes,
      shield: this.shield,
      slow: this.slow,
      stage: stageAt(this.dist).name,
      count: this.phase === "count" ? Math.ceil(this.countT) : 0,
      cross: this.phase === "play" && this.crossBusy() && this.crossing!.g.position.z < 2,
    });
  }

  private gameOver() {
    this.phase = "over";
    sfx.play("over");
    this.skier.visible = true;
    this.shield = 0;
    this.emitHud();
    this.cb.onOver({ score: this.score(), dist: Math.floor(this.dist), buns: this.buns });
  }

  private onKey = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement) return; // typing a name
    if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") this.steer(-1);
    else if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") this.steer(1);
    else return;
    e.preventDefault();
  };

  // Swipe left/right to change lane (one lane per ~30 px, so a long swipe can
  // cross two). Mostly vertical moves are ignored.
  private onDown = (e: PointerEvent) => {
    if (this.controls === "tap") {
      // Tap the left or right half of the screen to change lane
      const r = this.renderer.domElement.getBoundingClientRect();
      this.steer(e.clientX < r.left + r.width / 2 ? -1 : 1);
      return;
    }
    this.swipe = { id: e.pointerId, x: e.clientX, y: e.clientY };
    this.renderer.domElement.setPointerCapture(e.pointerId);
  };

  private onMove = (e: PointerEvent) => {
    const s = this.swipe;
    if (!s || s.id !== e.pointerId) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    const th = 30;
    if (Math.abs(dx) > th && Math.abs(dx) > Math.abs(dy)) {
      this.steer(dx < 0 ? -1 : 1);
      s.x = e.clientX;
      s.y = e.clientY;
    } else if (Math.abs(dy) > th) {
      s.x = e.clientX;
      s.y = e.clientY;
    }
  };

  private onUp = (e: PointerEvent) => {
    if (this.swipe?.id !== e.pointerId) return;
    this.swipe = null;
  };
}
